// Shared low-level parsers for KPI measurement values. The frequency
// synonym matcher and the boolean met/not-met classifier were each
// re-implemented inline across period.ts, summary.ts, autoRag.ts and
// formatKpiValue.ts. This leaf holds the matching logic once; callers
// keep their own fallback / output mapping (which legitimately differ —
// period.ts defaults unknown→monthly, summary.ts keeps an "unknown"
// bucket).

import { KPI_BOOLEAN_MET, KPI_BOOLEAN_NOT_MET } from "../../types";

export type CanonicalKpiFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually";

/**
 * Match a free-form frequency string to a canonical frequency, accepting
 * the established synonyms. Returns null when nothing matches so callers
 * can apply their own fallback (concrete default vs an "unknown" bucket).
 */
export function matchKpiFrequency(raw: string): CanonicalKpiFrequency | null {
  const v = raw.trim().toLowerCase();
  if (v === "daily" || v === "day") return "daily";
  if (v === "weekly" || v === "week") return "weekly";
  if (v === "monthly" || v === "month") return "monthly";
  if (v === "quarterly" || v === "quarter") return "quarterly";
  if (v === "annually" || v === "annual" || v === "yearly") return "annually";
  return null;
}

/**
 * Classify a boolean-KPI measurement value as met / not-met, or unknown
 * for empty / legacy / malformed input. Callers map these three outcomes
 * to their own domain (RAG colour, sparkline y, display label).
 */
export function classifyBooleanKpiValue(
  raw: string | null | undefined,
): "met" | "not-met" | "unknown" {
  const v = raw?.trim().toLowerCase();
  if (v === KPI_BOOLEAN_MET) return "met";
  if (v === KPI_BOOLEAN_NOT_MET) return "not-met";
  return "unknown";
}
